import differenceInDays from 'date-fns/differenceInDays'
import parseISO from 'date-fns/parseISO'
import {lensProp, map, over, pipe} from 'ramda'
import {TaxConfig} from 'types/Localization'

import {Room as SapiRoom, RoomPrice, RoomsSplitBooking} from '@findhotel/sapi'

import {Price as BookingPrice} from '../../../api-types/bovio/response/booking'
import {Price as OfferCheckPrice} from '../../../api-types/bovio/response/offer_check'
import {Offer, OfferPrice} from '../../../offer/types/offer'
import {convertPricesToNewFormat} from '../../../price/business/price'
import {BaseGetRoomsParams} from '../../../sapi/services/searchApi'
import {Room} from '../../types/room'
import {roomsToConfigurationObject} from '../roomConfiguration'
import {TransformOptions} from '.'
import {
  addCurrencyToChargeableAndHotelFees,
  basePath,
  convertHotelFeesBreakdownToFloat,
  convertPriceToFloat,
  decorateWithNightlyDisplayTotal,
  decorateWithNightlyPrice,
  guaranteeAtLeastOneHotelFeeBreakdown,
  hotelFeesNightlyPath,
  hotelFeesPath,
  nightlyBasePath,
  nightlyTaxesPath,
  taxesPath
} from './utils/price'

export type PriceType = RoomPrice | BookingPrice | OfferCheckPrice

/**
 * Returns number of nights between checkIn and checkout
 */
/**
 * Returns number of nights between checkIn and checkout
 * @param checkIn - Check-in date
 * @param checkOut - Check-out date
 * @returns Number of nights between check-in and check-out dates
 */
const getNumberOfNights = (checkIn: string, checkOut: string): number =>
  differenceInDays(parseISO(checkOut), parseISO(checkIn))

/**
 * Updates all price-related fields in a price object by converting them to floats and calculating nightly rates
 * @param price - The price object to update (RoomPrice, BookingPrice or OfferCheckPrice)
 * @param params - Object containing check-in date, check-out date and rooms configuration
 * @param params.checkIn - Check-in date
 * @param params.checkOut - Check-out date
 * @param params.rooms - Room configuration string
 * @param options - Tax configuration options
 * @returns Updated price object with converted values and nightly rates
 */
export const updateAllPrices = (
  price: PriceType,
  params: {
    checkIn: string
    checkOut: string
    rooms: string
  },
  options: TaxConfig
): OfferPrice => {
  const numberOfNights = getNumberOfNights(params.checkIn, params.checkOut)
  const {numberOfRooms} = roomsToConfigurationObject(params.rooms)

  return pipe<
    [PriceType],
    PriceType,
    PriceType,
    PriceType,
    PriceType,
    PriceType,
    PriceType,
    PriceType,
    OfferPrice
  >(
    price => convertPriceToFloat(basePath, price),
    price => convertPriceToFloat(taxesPath, price),
    price => convertPriceToFloat(hotelFeesPath, price),
    convertHotelFeesBreakdownToFloat,
    price =>
      decorateWithNightlyPrice(
        nightlyBasePath,
        basePath,
        price,
        numberOfNights,
        numberOfRooms
      ),
    price =>
      decorateWithNightlyPrice(
        nightlyTaxesPath,
        taxesPath,
        price,
        numberOfNights,
        numberOfRooms
      ),
    price =>
      decorateWithNightlyPrice(
        hotelFeesNightlyPath,
        hotelFeesPath,
        price,
        numberOfNights,
        numberOfRooms
      ),
    price =>
      decorateWithNightlyDisplayTotal(
        price,
        numberOfNights,
        numberOfRooms,
        options.includeTaxes,
        options.includeLocalTaxes
      ),
    addCurrencyToChargeableAndHotelFees,
    convertPricesToNewFormat,
    guaranteeAtLeastOneHotelFeeBreakdown
  )(price)
}

/**
 * Updates room prices by applying the provided update function to each price object
 * @param rooms - Array of rooms
 * @param updateFn - Function to transform each price object
 * @returns Array of rooms with updated prices
 */
export const deepUpdateRoomsPrices = (
  rooms: SapiRoom[],
  updateFn: (price: RoomPrice) => OfferPrice
): Room[] => {
  const offers = lensProp<Room, 'offers'>('offers')
  const prices = lensProp<Offer, 'prices'>('prices')

  // @ts-expect-error - mapping the types here was too complicated at time of writing
  return map(over(offers, map(over(prices, map(updateFn)))), rooms)
}

/**
 * Decorates room prices with additional price information like nightly rates, taxes, fees etc.
 * @param rooms - Array of rooms
 * @param params - Base parameters used for getting rooms
 * @param transformOptions - Options for transforming the prices
 * @returns Array of rooms with decorated prices
 */
export const decoratePrices = (
  rooms: SapiRoom[],
  params: BaseGetRoomsParams,
  transformOptions: TransformOptions
): Room[] =>
  deepUpdateRoomsPrices(rooms, price =>
    updateAllPrices(price, params, transformOptions)
  )

/**
 * Decorates split booking prices with additional price information like nightly rates, taxes, fees etc.
 * @param splitBooking - Split booking object containing offers and prices
 * @param params - Base parameters used for getting rooms
 * @param transformOptions - Options for transforming the prices
 * @returns Split booking with decorated prices
 */
export const decorateSplitBookingPrices = (
  splitBooking: RoomsSplitBooking,
  params: BaseGetRoomsParams,
  transformOptions: TransformOptions
) => {
  const updatedOffers = splitBooking.offers.map(item => {
    const transformedPrices = item.offer.prices.map(price =>
      updateAllPrices(price, params, transformOptions)
    )

    return {
      ...item,
      offer: {...item.offer, prices: transformedPrices}
    }
  })

  const updatedPrices = splitBooking.prices.map(price =>
    updateAllPrices(price, params, transformOptions)
  )

  return {...splitBooking, offers: updatedOffers, prices: updatedPrices}
}
